import { createAsyncThunk } from '@reduxjs/toolkit';
import { push } from 'redux-first-history';

import { showLoading, hideLoading } from '../app';
import { ROUTES } from '../../helpers/constants';
import ManagementAPIClient from '../../api/management';
import { toastr } from '../../components/CustomToast';

const authenticateManagementUser = createAsyncThunk(
  'management/authenticateManagementUser',
  async (_, { dispatch }) => {
    try {
      dispatch(showLoading());
      return { user: [], memberships: [] };
    } catch (err) {
      console.error('Authentication Error: ', err);
      dispatch(push(ROUTES.LANDING.path));
    } finally {
      dispatch(hideLoading());
    }
  }
);

const getManagementOrganizations = createAsyncThunk(
  'management/getManagementOrganizations',
  async (_, { dispatch }) => {
    try {
      dispatch(showLoading());
      const items = await new ManagementAPIClient().GET('/admin/organizations');
      const organizations = (items ?? []).map((org) => ({
        org_id: org.item_id,
        name: org.name,
      }));
      return { organizations };
    } catch (err) {
      console.error('Management Org Fetch Error: ', err);
    } finally {
      dispatch(hideLoading());
    }
  }
);

const getManagementUsers = createAsyncThunk(
  'management/getManagementUsers',
  async (_, { dispatch }) => {
    try {
      dispatch(showLoading());
      const items = await new ManagementAPIClient().GET('/users');
      const users = (items ?? []).map((u) => ({
        user_id: u.item_id,
        name: u.name,
        email: u.email,
      }));
      return { users };
    } catch (err) {
      console.error('Management Users Fetch Error: ', err);
    } finally {
      dispatch(hideLoading());
    }
  }
);

const createManagementOrg = createAsyncThunk(
  'management/createManagementOrg',
  async (name, { dispatch }) => {
    try {
      dispatch(showLoading());
      const result = await new ManagementAPIClient().POST(
        '/admin/organizations',
        { name }
      );
      const organizations = (result?.value ?? []).map((org) => ({
        org_id: org.item_id,
        name: org.name,
      }));
      const newOrg = result?.new
        ? { org_id: result.new.item_id, name: result.new.name }
        : null;
      return { organizations, newOrg };
    } catch (err) {
      console.error('Management Org Create Error: ', err);
      throw err;
    } finally {
      dispatch(hideLoading());
    }
  }
);

// Adds (or links, if the Cognito account already exists) a user to an
// EventSync org with the given role. No equivalent existed in admin-dashboard
// before — Gridcap and LightLevel already had postDemandMembership /
// postSolarMembership; the EventSync Memberships admin page was a stub.
const createManagementOrgUser = createAsyncThunk(
  'management/createManagementOrgUser',
  async ({ orgId, email, name, administrator, editor }, { dispatch }) => {
    try {
      dispatch(showLoading());
      const result = await new ManagementAPIClient().POST(
        `/organizations/${orgId}/users`,
        { email, name, administrator, editor }
      );
      toastr.success({ title: 'User added to EventSync org' });
      return result;
    } catch (err) {
      console.error('Management Org User Create Error: ', err);
      throw err;
    } finally {
      dispatch(hideLoading());
    }
  }
);

const getManagementSites = createAsyncThunk(
  'management/getManagementSites',
  async (_, { dispatch }) => {
    try {
      dispatch(showLoading());
      const items = await new ManagementAPIClient().GET('/admin/sites');
      return { sites: items ?? [] };
    } catch (err) {
      console.error('Management Sites Fetch Error: ', err);
    } finally {
      dispatch(hideLoading());
    }
  }
);

const getManagementStats = createAsyncThunk(
  'management/getManagementStats',
  async (_, { dispatch }) => {
    try {
      dispatch(showLoading());
      const data = await new ManagementAPIClient().GET('/admin/stats');
      return { stats: data ?? {} };
    } catch (err) {
      console.error('Management Stats Fetch Error: ', err);
    } finally {
      dispatch(hideLoading());
    }
  }
);

export {
  authenticateManagementUser,
  getManagementOrganizations,
  getManagementUsers,
  createManagementOrg,
  createManagementOrgUser,
  getManagementStats,
  getManagementSites,
};
